/**
 * Lead Card Component
 * Displays lead information in a card format for grid/list views
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business,
  Phone,
  Email,
  TrendingUp,
  MoreVert,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import AIScoreBadge from './AIScoreBadge';
import { Lead } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface LeadCardProps {
  lead: Lead;
  onClick?: (lead: Lead) => void;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>, lead: Lead) => void;
  variant?: 'compact' | 'detailed';
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  onClick,
  onMenuClick,
  variant = 'detailed',
}) => {
  // Status color mapping
  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'NEW':
        return 'primary';
      case 'CONTACTED':
        return 'default';
      case 'QUALIFIED':
        return 'success';
      case 'UNQUALIFIED':
      case 'LOST':
        return 'error';
      case 'CONVERTED':
        return 'success';
      default:
        return 'default';
    }
  };

  // Status label mapping
  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      NEW: 'New',
      CONTACTED: 'Contacted',
      QUALIFIED: 'Qualified',
      UNQUALIFIED: 'Unqualified',
      CONVERTED: 'Converted',
      LOST: 'Lost',
    };
    return labels[status] || status;
  };

  // Priority color mapping
  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'URGENT':
        return '#ff4d4f';
      case 'HIGH':
        return '#faad14';
      case 'MEDIUM':
        return '#1890ff';
      case 'LOW':
        return '#52c41a';
      default:
        return '#d9d9d9';
    }
  };

  // Priority label mapping
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      URGENT: 'Urgent',
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
    };
    return labels[priority] || priority;
  };

  // Source label mapping
  const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
      LINKEDIN: 'LinkedIn',
      WEBSITE: 'Website',
      REFERRAL: 'Referral',
      EVENT: 'Event',
      COLD_CALL: 'Cold Call',
      SOCIAL_MEDIA: 'Social Media',
      OTHER: 'Other',
    };
    return labels[source] || source;
  };

  // Format currency
  const formatCurrency = (amount?: number | null): string => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Handle card click
  const handleCardClick = () => {
    if (onClick) {
      onClick(lead);
    }
  };

  // Handle menu click
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (onMenuClick) {
      onMenuClick(event, lead);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          boxShadow: 6,
          transform: 'translateY(-4px)',
        } : {},
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header with AI Score and Menu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            {/* Lead Number */}
            <Typography variant="caption" color="text.secondary" display="block">
              {lead.leadNo}
            </Typography>

            {/* Contact Name */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
              {lead.contactName}
            </Typography>

            {/* Company Name */}
            {lead.companyName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {lead.companyName}
                </Typography>
              </Box>
            )}
          </Box>

          {/* AI Score Badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lead.aiScore !== undefined && lead.aiScore !== null && (
              <AIScoreBadge
                score={lead.aiScore}
                factors={lead.scoreHistory?.[0]?.scoringFactors}
                size="small"
              />
            )}
            {onMenuClick && (
              <IconButton size="small" onClick={handleMenuClick}>
                <MoreVert />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Contact Info */}
        {variant === 'detailed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
            {lead.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {lead.email}
                </Typography>
              </Box>
            )}
            {lead.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {lead.phone}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Status and Priority Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={getStatusLabel(lead.status)}
            color={getStatusColor(lead.status)}
            size="small"
          />
          <Chip
            label={getPriorityLabel(lead.priority)}
            size="small"
            sx={{
              backgroundColor: getPriorityColor(lead.priority),
              color: '#fff',
            }}
          />
          {lead.source && (
            <Chip
              label={getSourceLabel(lead.source)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Financial Info and Timeline */}
        {variant === 'detailed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Estimated Budget */}
            {lead.estimatedBudget && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachMoney sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="body2" fontWeight="medium">
                  Budget: {formatCurrency(lead.estimatedBudget)}
                </Typography>
              </Box>
            )}

            {/* Last Contacted */}
            {lead.lastContactedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Last Contact:{' '}
                  {formatDistanceToNow(new Date(lead.lastContactedAt), {
                    addSuffix: true,
                  })}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* AI Suggestion Preview */}
        {variant === 'detailed' && lead.aiScoreReason && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: 'rgba(24, 144, 255, 0.05)',
              borderLeft: '3px solid #1890ff',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <TrendingUp sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" fontWeight="bold" color="primary">
                AI Scoring Factors
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {typeof lead.aiScoreReason === 'object'
                ? Object.entries(lead.aiScoreReason as Record<string, any>)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(', ')
                : lead.aiScoreReason}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Card Actions (optional) */}
      {variant === 'detailed' && lead.assignedTo && (
        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
          <Tooltip title={`Assigned to: ${lead.assignedTo.name}`}>
            <Chip
              label={lead.assignedTo.name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Tooltip>
        </CardActions>
      )}
    </Card>
  );
};

export default React.memo(LeadCard);
